import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { SxProps } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import AssignedTagsList from "components/assigned-tags";
import CircularProgress from "components/circular-progress";
import { TagTypes } from "constants/tags";
import { selectCurrentUser } from "features/auth/auth-slice";
import {
  useAddAssignedTagMutation,
  useGetPatientAssignedTagsQuery,
  useLazyGetPatientAssignedTagsQuery,
  useRemoveAssignedTagMutation
} from "services/assigned-tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";

type HeaderAssignedTagsProps = {
  patientId: number;
  sx?: SxProps;
  preloadedTags?: any[];
  updateItem?: (id: number, data: { assigned_tags: any[] }) => void;
  skipQuery?: boolean;
};

export default function PatientAssignedTags({
  patientId,
  sx,
  preloadedTags,
  updateItem,
  skipQuery = false
}: HeaderAssignedTagsProps) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);

  // Skip query if we have preloadedTags (either same patient or new patient with new tags)
  const shouldSkip = Boolean(skipQuery && preloadedTags) || !patientId;

  const {
    currentData: tags,
    isFetching,
    refetch
  } = useGetPatientAssignedTagsQuery(
    {
      object_id: patientId
    },
    {
      skip: shouldSkip
    }
  );

  const [triggerLazy, { data: lazyData, isFetching: isFetchingLazyData }] =
    useLazyGetPatientAssignedTagsQuery();

  const prevPatientIdRef = useRef<number>();
  const lazyDataPatientIdRef = useRef<number>();

  useEffect(() => {
    const hasPatientChanged = prevPatientIdRef.current !== patientId;

    if (hasPatientChanged) {
      lazyDataPatientIdRef.current = undefined;
      prevPatientIdRef.current = patientId;
    }
  }, [patientId]);

  const initialTags = useMemo(() => {
    // Only use lazyData if it belongs to the current patient
    const validLazyData =
      lazyDataPatientIdRef.current === patientId ? lazyData : undefined;

    return (
      validLazyData ??
      tags ??
      (shouldSkip ? preloadedTags : undefined) ??
      undefined
    );
  }, [lazyData, tags, shouldSkip, preloadedTags, patientId]);

  const [patientTagsById, setPatientTagsById] = useState<
    Record<number, any[] | undefined>
  >({});

  // Initialize tags for current patient if not already set
  useEffect(() => {
    if (patientId && !isFetchingLazyData && !isFetching) {
      setPatientTagsById((prev) => {
        // Only update if the patient doesn't have tags set or if initialTags changed
        if (!prev[patientId] || prev[patientId] !== initialTags) {
          return {
            ...prev,
            [patientId]: initialTags
          };
        }
        return prev;
      });
    }
  }, [patientId, initialTags, isFetchingLazyData, isFetching]);

  // Get current patient's tags
  const currentPatientTags = patientTagsById[patientId] || [];

  const fetchFresh = async () => {
    try {
      const currentPatientId = patientId;
      const fresh = shouldSkip
        ? await triggerLazy({ object_id: currentPatientId }).unwrap()
        : await refetch().unwrap();

      if (shouldSkip) {
        lazyDataPatientIdRef.current = currentPatientId;
      }
      setPatientTagsById((prev) => ({
        ...prev,
        [currentPatientId]: fresh
      }));
      updateItem?.(currentPatientId, { assigned_tags: fresh });
      return fresh;
    } catch {
      // RTK error #38 ("Cannot refetch a query that has not been started yet")
      // is thrown when refetch() is called on a skipped query. Network errors
      // from .unwrap() also land here. Either way the UI keeps working with
      // the optimistic / stale data already in state.
    }
  };

  const [addTag] = useAddAssignedTagMutation();
  const [removeTag] = useRemoveAssignedTagMutation();

  const handleUpdate = useCallback(
    (tag: AssignedTag) => {
      setPatientTagsById((prev) => ({
        ...prev,
        [patientId]: prev[patientId]?.map((assignedTag) =>
          assignedTag.id === tag.id ? tag : assignedTag
        )
      }));
    },
    [patientId]
  );

  const handleAdd = async (tag_id: number, tag: Tag) => {
    const response = await addTag({
      tag: tag_id,
      tag_type: TagTypes.PATIENT,
      object_id: patientId
    });

    if ("data" in response) {
      const newTag = {
        ...response.data,
        tag,
        created_by: currentUser as any,
        updated_by: currentUser as any
      };

      setPatientTagsById((prev) => ({
        ...prev,
        [patientId]: [...(prev[patientId] || []), newTag]
      }));

      dispatch(setCustomMessage("Tag added"));
      dispatch(setOpen(1500));
      await fetchFresh();
    }
  };

  const handleDelete = async (tag: number) => {
    const response = await removeTag({ tag, tag_type: TagTypes.PATIENT });

    if ("data" in response) {
      // Optimistically update local state for current patient
      setPatientTagsById((prev) => ({
        ...prev,
        [patientId]:
          prev[patientId]?.filter((assignedTag) => assignedTag.id !== tag) || []
      }));
      dispatch(setCustomMessage("Tag removed"));
      dispatch(setOpen(1500));
      await fetchFresh();
    }
  };

  if (!skipQuery && isFetching) {
    return <CircularProgress sx={{ transform: "scale(0.6)", mt: -3, py: 0 }} />;
  }

  return (
    <AssignedTagsList
      sx={{ mb: -1, ...sx }}
      tagType={TagTypes.PATIENT}
      tags={currentPatientTags}
      modelId={patientId}
      onAdd={handleAdd}
      onDelete={handleDelete}
      onUpdate={handleUpdate}
    />
  );
}
